import React from 'react';
import { motion } from 'framer-motion';
import type { RegionData } from './MapData';

interface MapRegionProps {
  regionData: RegionData;
  onClick: (regionId: string) => void;
}

type RegionLabelConfig = {
  lines: string[];
  fontSize?: number;
  lineHeight?: number;
  textPosition?: { x: number; y: number };
};

const REGION_LABELS: Record<string, RegionLabelConfig> = {
  Noroeste: {
    lines: ['NOROESTE E', 'ALTO PARANAÍBA'],
    fontSize: 14,
    lineHeight: 14,
    textPosition: { x: 365, y: 168 },
  },
  Norte: {
    lines: ['NORTE'],
  },
  Centro: {
    lines: ['CENTRO'],
  },
  Sul: {
    lines: ['SUL'],
  },
  Jequitinhonha: {
    lines: ['JEQUITINHONHA', 'E MUCURI'],
    fontSize: 13,
    lineHeight: 13,
    textPosition: { x: 695, y: 244 },
  },
  'Centro-oeste': {
    lines: ['CENTRO-OESTE', 'E SUDOESTE'],
    fontSize: 13,
    lineHeight: 13,
    textPosition: { x: 402, y: 458 },
  },
  'Rio-doce': {
    lines: ['RIO DOCE E', 'VALE DO AÇO'],
    fontSize: 13,
    lineHeight: 13,
    textPosition: { x: 680, y: 395 },
  },
  'Zona-da-mata': {
    lines: ['ZONA DA MATA'],
    fontSize: 14,
    lineHeight: 14,
    textPosition: { x: 582, y: 525 },
  },
  Triangulo: {
    lines: ['TRIÂNGULO'],
    fontSize: 14,
  },
};

const MapRegion: React.FC<MapRegionProps> = ({ regionData, onClick }) => {
  const handleClick = () => {
    onClick(regionData.id);
  };

  const labelConfig = REGION_LABELS[regionData.id];
  const labelLines = labelConfig?.lines || [regionData.id.toUpperCase()];
  const labelPosition = labelConfig?.textPosition || regionData.textPosition;
  const fontSize = labelConfig?.fontSize || regionData.textSize;
  const lineHeight = labelConfig?.lineHeight || Math.max(fontSize * 0.95, 14);
  const firstLineOffset = labelLines.length > 1 ? -((labelLines.length - 1) * lineHeight) / 2 : 0;

  return (
    <>
      {/* Path da região */}
      <motion.path
        id={regionData.id}
        d={regionData.path}
        className={`${regionData.colors.fill} hover:${regionData.colors.hover} transition-colors duration-300 cursor-pointer filter brightness-100 hover:brightness-105 hover:shadow-lg`}
        onClick={handleClick}
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.2 }}
      />
      
      {/* Texto da região */}
      <text
        x={labelPosition.x}
        y={labelPosition.y}
        textAnchor="middle"
        fontWeight="bold"
        fontSize={fontSize}
        fill="oklch(55.4% 0.046 257.417)"
        stroke="rgba(255,255,255,0.9)"
        strokeWidth="2"
        paintOrder="stroke"
        letterSpacing="0.02em"
        className="select-none cursor-pointer uppercase"
        onClick={handleClick}
      >
        {labelLines.map((line, index) => (
          <tspan
            key={`${regionData.id}-${line}-${index}`}
            x={labelPosition.x}
            dy={index === 0 ? firstLineOffset : lineHeight}
          >
            {line}
          </tspan>
        ))}
      </text>
    </>
  );
};

export default MapRegion; 
